'use client';

import { useEffect, useCallback, useMemo, useRef } from 'react';
import {
  useCartStore,
  selectPromoEligiblePrice,
  getAvailableStatusRoll,
  type BonusType,
} from '@/stores/cart-store';
import { useStatusRolls } from '@/hooks/use-status-rolls';
import { useMoneyRules } from '@/lib/money-rules';

interface StatusRollProduct {
  id: number;
  name: string;
  image_url: string;
  sku: string;
  type: BonusType;
  label: string;
}

/**
 * Начисление статусного ролла по сумме корзины.
 *
 * НИЧЕГО НЕ РИСУЕТ. Шкалы «до следующего ролла» убраны из корзины: ролл
 * появляется в списке товаров обычной строкой-подарком, сам повышается
 * silver → gold → platinum при росте суммы и снимается, если сумма упала
 * ниже порога.
 *
 * Компонент обязан оставаться смонтированным в корзине — вся механика живёт
 * в его эффекте, и без него роллы перестанут начисляться.
 */
export function StatusRollAutoAward() {
  const promoEligiblePrice = useCartStore(selectPromoEligiblePrice);
  const addStatusRoll = useCartStore((state) => state.addStatusRoll);
  const removeStatusRolls = useCartStore((state) => state.removeStatusRolls);
  const cartItems = useCartStore((state) => state.items);

  // Данные роллов берём из общего каталога (useProducts) — без отдельных запросов /products/:id
  const { rolls: rollProducts, isLoading } = useStatusRolls();
  // Какой товар выдаётся за какой статус — с сервера (админка «Деньги»),
  // а не зашитыми id: при смене поставки подарок меняется без релиза.
  const { status_rolls: statusRollRules } = useMoneyRules();

  const statusRolls = useMemo<StatusRollProduct[]>(
    () =>
      statusRollRules.map((rule) => {
        const product = rollProducts[rule.type];
        const fallbackName =
          rule.type === 'silver' ? 'Silver Ролл' : rule.type === 'gold' ? 'Gold Ролл' : 'Platinum Ролл';
        return {
          id: rule.product_id,
          name: product?.name || fallbackName,
          image_url: product?.image_url || '',
          sku: product?.sku || '',
          type: rule.type as BonusType,
          label: rule.type.charAt(0).toUpperCase() + rule.type.slice(1),
        };
      }),
    [rollProducts, statusRollRules]
  );

  // Какой статусный ролл уже лежит в корзине
  const currentStatusRollType = useMemo(() => {
    const statusRoll = cartItems.find(
      (item) =>
        item.bonusType === 'silver' || item.bonusType === 'gold' || item.bonusType === 'platinum'
    );
    return statusRoll?.bonusType || null;
  }, [cartItems]);

  // Какой ролл положен по текущей сумме
  const targetRoll = useMemo(() => {
    return getAvailableStatusRoll(promoEligiblePrice);
  }, [promoEligiblePrice]);

  // Защита от зацикливания: не пересчитываем, если ничего не изменилось
  const lastProcessedRef = useRef<{ type: string | null; price: number }>({ type: null, price: 0 });

  const updateStatusRoll = useCallback(() => {
    if (
      lastProcessedRef.current.type === (targetRoll?.type || null) &&
      Math.abs(lastProcessedRef.current.price - promoEligiblePrice) < 1
    ) {
      return;
    }

    lastProcessedRef.current = { type: targetRoll?.type || null, price: promoEligiblePrice };

    if (targetRoll) {
      // Сумма выросла до следующего порога — меняем ролл на более высокий
      if (currentStatusRollType !== targetRoll.type) {
        const rollData = statusRolls.find((r) => r.type === targetRoll.type);
        if (rollData) {
          addStatusRoll(
            {
              id: rollData.id,
              name: rollData.name,
              price: 0,
              image_url: rollData.image_url,
              is_available: true,
              status: 'available',
              spiciness: 'none',
              sku: rollData.sku || '',
              description: `Статусный ролл ${rollData.label}`,
              categories: [],
            },
            targetRoll.type
          );
        }
      }
    } else if (currentStatusRollType) {
      // Сумма упала ниже минимального порога — снимаем ролл
      removeStatusRolls();
    }
  }, [
    targetRoll,
    currentStatusRollType,
    statusRolls,
    promoEligiblePrice,
    addStatusRoll,
    removeStatusRolls,
  ]);

  useEffect(() => {
    if (!isLoading && statusRolls.length > 0) {
      // requestAnimationFrame — чтобы обновление корзины не дёргало анимацию списка
      const rafId = requestAnimationFrame(() => {
        updateStatusRoll();
      });
      return () => cancelAnimationFrame(rafId);
    }
  }, [isLoading, statusRolls.length, updateStatusRoll]);

  return null;
}
